
import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import App from './App'
import reportWebVitals from './reportWebVitals'
import 'react-app-polyfill/stable';
import { initRUM } from './utils/rum';
import { initFrontendMonitoring } from './utils/frontendMonitoring';
import { getConfig } from './configs/edition.config';
import { installMaintenanceObserver } from './services/maintenance';

// ResizeObserver 에러 완전히 무시 처리 (React 에러 오버레이 포함)
const resizeObserverErrors = [
  /ResizeObserver loop limit exceeded/i,
  /ResizeObserver loop completed with undelivered notifications/i,
  /ResizeObserver/i  // 모든 ResizeObserver 관련 에러 차단
];

const isResizeObserverError = (error) => {
  const errorMessage = error?.message || error?.error?.message || String(error);
  return resizeObserverErrors.some(pattern => pattern.test(errorMessage));
};

// 1. window.error 이벤트 핸들러 (최우선 실행)
window.addEventListener('error', (e) => {
  if (isResizeObserverError(e)) {
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
    return false;
  }
}, true); // capture phase에서 먼저 실행

// 2. unhandledrejection 이벤트 핸들러 (Promise 에러)
window.addEventListener('unhandledrejection', (e) => {
  if (isResizeObserverError(e.reason)) {
    e.preventDefault();
    e.stopImmediatePropagation();
  }
}, true);

// 3. ResizeObserver 자체를 래핑하여 에러 방지
if (typeof ResizeObserver !== 'undefined') {
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback) {
      // 콜백을 안전하게 래핑
      const safeCallback = (...args) => {
        window.requestAnimationFrame(() => {
          try {
            callback(...args);
          } catch (e) {
            // ResizeObserver 에러만 무시, 다른 에러는 throw
            if (!isResizeObserverError(e)) {
              console.error('ResizeObserver callback error:', e);
            }
          }
        });
      };
      super(safeCallback);
    }
  };
}

// 4. React Error Overlay 무시 처리
if (process.env.NODE_ENV === 'development') {
  const originalConsoleError = console.error;
  console.error = (...args) => {
    // ResizeObserver 에러는 콘솔에도 표시하지 않음
    if (args[0] && isResizeObserverError(args[0])) {
      return;
    }
    originalConsoleError.apply(console, args);
  };
}

// 큐베이스 점검(쓰기 동결) 감지 — 전역 fetch 관찰자. RUM 이 fetch 를 감싸기 전에 설치해야 RUM 해제 시에도 남는다.
// 설치형(Standalone)은 큐베이스를 부르지 않으므로 설치하지 않는다.
if (!getConfig().isStandalone) {
  installMaintenanceObserver();
}

// 프론트엔드 오류 수집 (웹 전용) — QPM RUM SDK(플랫폼 경로로 제공) → QPM. 첫 렌더 전에 시작해야 초기 오류를 잃지 않는다.
// 설치형은 Electron preload 가 로그 파일로 남기므로 여기서 아무것도 하지 않는다.
// context 는 모든 수집 기록에 실리는 태그 — QPM 에서 에디션별로 나눠 볼 수 있다.
initFrontendMonitoring({
  beApi: process.env.REACT_APP_BE_API,
  isStandalone: getConfig().isStandalone,
  context: { deployment: 'cloud', edition: getConfig().edition },
});

const root = ReactDOM.createRoot(document.getElementById('root'))
root.render(
    <App />
    // <React.StrictMode>
    // </React.StrictMode>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()

// RUM (Real User Monitoring) SDK 초기화
//
// ⚠️ QPM 기능 전체를 꺼 둔 상태라 이 호출은 아무 일도 하지 않는다 (lib/qpm-sdk/enabled.js).
// 다시 켤 때 설정이 남아 있도록 호출은 그대로 둔다.
initRUM({
    endpoint: process.env.REACT_APP_QPM_ENDPOINT || 'https://opendatamodeler.com:3006/api/client-metrics',
    appName: 'opendatamodeler',
    appVersion: process.env.REACT_APP_VERSION || '1.3.3',
    sampleRate: 1.0,
    enableAutoTracking: true,
    enableWebVitals: true,
    enableErrorTracking: true,
    enablePerformanceTracking: true,
    debug: process.env.NODE_ENV === 'development',
    enabled: false  // QPM_ENABLED 가 true 가 되어도 여기서 다시 켜 줘야 한다
});

// fetch 자동 추적은 RUM 활성화 시에만 동작 (setRUMEnabled(true) 호출 시)
